import React, { useLayoutEffect, useRef, useState } from 'react'
import { Tag, Tooltip } from 'antd'
import type { OrgChip } from '../../types'

interface OrgChipListProps {
  chips: OrgChip[]
  maxVisible?: number
}

const OrgChipList: React.FC<OrgChipListProps> = ({ chips, maxVisible }) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const measureRefs = useRef<Array<HTMLSpanElement | null>>([])
  const moreMeasureRef = useRef<HTMLSpanElement>(null)
  const [visibleCount, setVisibleCount] = useState(chips.length)
  const visibleChips = chips.slice(0, visibleCount)
  const overflowCount = chips.length - visibleChips.length
  const tooltipTitle = (
    <div className="um-refactor__chip-tooltip">
      {chips.map((chip) => (
        <div key={chip.id}>{chip.name || chip.label}</div>
      ))}
    </div>
  )

  useLayoutEffect(() => {
    const container = containerRef.current
    if (!container) return undefined

    const calculateVisibleCount = () => {
      const availableWidth = container.clientWidth
      const chipWidths = chips.map((_, index) => measureRefs.current[index]?.offsetWidth || 0)
      const moreWidth = moreMeasureRef.current?.offsetWidth || 0
      const gap = 6

      const allChipsWidth = chipWidths.reduce((total, width) => total + width, 0) + gap * Math.max(chips.length - 1, 0)
      if (allChipsWidth <= availableWidth && (!maxVisible || chips.length <= maxVisible)) {
        setVisibleCount(chips.length)
        return
      }

      let width = moreWidth
      let nextVisibleCount = 0
      for (let index = 0; index < chipWidths.length; index += 1) {
        if (maxVisible && index >= maxVisible) break
        const nextWidth = width + chipWidths[index] + gap
        if (nextWidth > availableWidth) break
        width = nextWidth
        nextVisibleCount = index + 1
      }
      setVisibleCount(maxVisible ? Math.min(nextVisibleCount, maxVisible) : nextVisibleCount)
    }

    calculateVisibleCount()
    const observer = new ResizeObserver(calculateVisibleCount)
    observer.observe(container)
    return () => observer.disconnect()
  }, [chips, maxVisible])

  return (
    <div ref={containerRef} className="um-refactor__chips-cell">
      <Tooltip title={tooltipTitle} overlayClassName="um-refactor__chip-tooltip-overlay">
        <div className="um-refactor__chips-trigger">
          <div className="um-refactor__chips">
            {visibleChips.map((chip) => (
              <Tag key={chip.id} className="um-refactor__chip">
                <span className="um-refactor__chip-dot" style={{ background: chip.color }} />
                {chip.name}
              </Tag>
            ))}
            {overflowCount > 0 && (
              <Tag className="um-refactor__chip um-refactor__chip--more">+{overflowCount}</Tag>
            )}
          </div>
        </div>
      </Tooltip>
      <div className="um-refactor__chip-measure" aria-hidden="true">
        {chips.map((chip, index) => (
          <Tag
            key={chip.id}
            className="um-refactor__chip"
            ref={(element) => {
              measureRefs.current[index] = element
            }}
          >
            <span className="um-refactor__chip-dot" style={{ background: chip.color }} />
            {chip.name}
          </Tag>
        ))}
        <Tag className="um-refactor__chip um-refactor__chip--more" ref={moreMeasureRef}>
          +{chips.length}
        </Tag>
      </div>
    </div>
  )
}

export default OrgChipList
